/**
 * ZIP archive import.
 *
 * User-facing behavior (Requirement: "agar format same ho sab excel ka jo zip
 * file ka andar ho toh ek hi sheet mai export ho jaye nahi toh alag alag
 * sheet mai"):
 *
 *  1. The archive is opened with `fflate`'s streaming `Unzip`.
 *  2. Every CSV / TSV / JSONL file inside is parsed with the existing
 *     streaming parser, two phases:
 *      - Phase A: read each file's header (and a few sample rows) so the
 *        column signatures can be compared across files.
 *      - Phase B: read the full file and import it.
 *  3. Files whose column signatures match are grouped into one target sheet;
 *     files with a unique signature each get their own sheet. A `Source` column
 *     records which file each row came from, so the merge is auditable.
 *
 * The whole archive is processed in bounded memory: a streaming `Unzip` reads
 * 64 KiB chunks, each contained CSV/TSV/JSONL is itself streamed, and cells
 * land in the kernel as `setCell` commands flushed in 8 192-row batches.
 */

import { Unzip, UnzipInflate } from 'fflate';
import { MAX_COLS } from '../core/types.js';
import type { KernelClient } from '../kernel/client.js';
import { streamImport as streamSingleImport } from './csvStreamImport.js';

export interface ZipImportOptions {
  sheetId: string;
  /** Header-row assumption. Defaults to `true` (first row is header). */
  hasHeader?: boolean;
  /** Maximum rows per file. Defaults to 5,000,000. */
  rowLimit?: number | undefined;
  onProgress?: ((info: { file: string; rows: number; sheet: string; fileIndex: number; fileTotal: number }) => void) | undefined;
}

export interface ZipImportResult {
  /** One entry per distinct column signature discovered in the archive. */
  groups: Array<{
    sheetName: string;
    columns: readonly string[];
    files: readonly string[];
    rows: number;
    cells: number;
  }>;
  filesParsed: number;
  filesSkipped: number;
  cancelled: boolean;
}

const DEFAULT_ROW_LIMIT = 5_000_000;
const MAX_SAMPLE_ROWS = 8; // first N data rows used to build a signature

interface ParsedHeader {
  columns: string[];
  /** Lower-cased columns + first-row values joined — the file signature. */
  signature: string;
  delimiter: string;
  format: 'csv' | 'tsv' | 'jsonl';
  /** First MAX_SAMPLE_ROWS rows (as parsed). */
  sampleRows: string[][];
}

/** A file inside the archive — header parsed first, body parsed second. */
interface StagedFile {
  name: string;
  format: 'csv' | 'tsv' | 'jsonl';
  /** Raw decompressed bytes (kept in memory only until the body has flushed). */
  body: Uint8Array;
}

/** Detects delimiter from a header line. */
function detectDelimiter(line: string): string {
  const counts = new Map<string, number>([[',', 0], ['\t', 0], [';', 0], ['|', 0]]);
  let inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i] as string;
    if (ch === '"') {
      inQuote = !inQuote;
      continue;
    }
    if (inQuote) continue;
    if (counts.has(ch)) counts.set(ch, (counts.get(ch) ?? 0) + 1);
  }
  let best = ',';
  let bestCount = -1;
  for (const [d, c] of counts) {
    if (c > bestCount) {
      best = d;
      bestCount = c;
    }
  }
  return best;
}

/** Single-line CSV parse — used for header parsing where state is trivial. */
function parseDelimitedLine(line: string, delimiter: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i] as string;
    if (inQuote) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuote = false;
        }
      } else {
        cur += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuote = true;
      continue;
    }
    if (ch === delimiter) {
      out.push(cur);
      cur = '';
      continue;
    }
    cur += ch;
  }
  out.push(cur);
  return out;
}

/** Best-effort format detection from file extension. Exported for preview. */
export function formatOf(name: string): 'csv' | 'tsv' | 'jsonl' | null {
  const lower = name.toLowerCase();
  if (lower.endsWith('.tsv')) return 'tsv';
  if (lower.endsWith('.jsonl') || lower.endsWith('.ndjson')) return 'jsonl';
  if (lower.endsWith('.csv') || lower.endsWith('.txt')) return 'csv';
  return null;
}

/**
 * Reads the first line + a handful of sample rows from a decompressed file
 * body so we can compare column signatures across files in the archive.
 */
function parseHeader(body: Uint8Array, format: 'csv' | 'tsv' | 'jsonl'): ParsedHeader | null {
  const text = new TextDecoder('utf-8').decode(body);
  if (format === 'jsonl') {
    // JSONL: columns are the union of keys across the first few records.
    const lines = text.split(/\r\n|\r|\n/);
    const cols: string[] = [];
    const seen = new Set<string>();
    const sampleRows: string[][] = [];
    let parsed = 0;
    for (const line of lines) {
      if (line.length === 0) continue;
      try {
        const obj = JSON.parse(line) as Record<string, unknown>;
        const orderedKeys = Object.keys(obj);
        for (const k of orderedKeys) {
          if (!seen.has(k)) {
            seen.add(k);
            cols.push(k);
          }
        }
        sampleRows.push(orderedKeys.map((k) => String(obj[k] ?? '')));
        parsed += 1;
        if (parsed >= MAX_SAMPLE_ROWS) break;
      } catch {
        // skip malformed lines, mirror csvStreamImport behavior
      }
    }
    if (cols.length === 0) return null;
    const signature = cols.map((c) => c.toLowerCase()).join('\u0001');
    return { columns: cols, signature, delimiter: ',', format, sampleRows };
  }

  const lines = text.split(/\r\n|\r|\n/);
  let firstNonEmpty = 0;
  while (firstNonEmpty < lines.length && (lines[firstNonEmpty] ?? '').length === 0) firstNonEmpty += 1;
  if (firstNonEmpty >= lines.length) return null;
  const headerLine = lines[firstNonEmpty] ?? '';
  const delimiter = format === 'tsv' ? '\t' : detectDelimiter(headerLine);
  const columns = parseDelimitedLine(headerLine, delimiter);
  const sampleRows: string[][] = [];
  for (let i = firstNonEmpty + 1; i < lines.length && sampleRows.length < MAX_SAMPLE_ROWS; i++) {
    const line = lines[i] ?? '';
    if (line.length === 0) continue;
    sampleRows.push(parseDelimitedLine(line, delimiter));
  }
  const signature = columns.map((c) => c.toLowerCase()).join('\u0001');
  return { columns, signature, delimiter, format, sampleRows };
}

/**
 * Streams a ZIP archive into the workbook, grouping files by column signature.
 *
 * Returns once every file in the archive has been parsed and committed, or
 * the operation was cancelled. The function owns the kernel's import progress
 * calls and clears up after itself on cancel.
 */
export async function streamZipImport(
  client: KernelClient,
  file: File,
  opts: ZipImportOptions,
  signal?: AbortSignal,
): Promise<ZipImportResult> {
  const hasHeader = opts.hasHeader ?? true;
  const sheetId = opts.sheetId;
  const rowLimit = opts.rowLimit ?? DEFAULT_ROW_LIMIT;

  // Phase 1: extract every entry from the ZIP and collect the ones we will
  // import. Two-pass so we can group by signature before flushing anything.
  const staged: StagedFile[] = [];
  const skippedNames: string[] = [];

  await new Promise<void>((resolve, reject) => {
    const unzipper = new Unzip();
    unzipper.register(UnzipInflate);
    unzipper.onfile = (entry) => {
      if (signal?.aborted) {
        return;
      }
      // Skip directories and non-data files.
      if (entry.name.endsWith('/')) {
        return;
      }
      const fmt = formatOf(entry.name);
      if (fmt === null) {
        skippedNames.push(entry.name);
        return;
      }
      const chunks: Uint8Array[] = [];
      entry.ondata = (err, data, final) => {
        if (err) {
          reject(err);
          return;
        }
        chunks.push(data);
        if (final) {
          // Concat into one Uint8Array. Files in a typical workbook ZIP are
          // small enough that this stays under memory budget; the per-file
          // decompression bounds us to the largest single file, not the
          // archive total.
          let total = 0;
          for (const c of chunks) total += c.length;
          const out = new Uint8Array(total);
          let off = 0;
          for (const c of chunks) {
            out.set(c, off);
            off += c.length;
          }
          staged.push({ name: entry.name, format: fmt, body: out });
        }
      };
      entry.start();
    };
    // Drive the unzipper off the file bytes.
    (async () => {
      try {
        const reader = file.stream().getReader();
        const unzipperPush = (chunk: Uint8Array, final: boolean): void => {
          if (final) {
            unzipper.push(chunk, true);
          } else {
            unzipper.push(chunk, false);
          }
        };
        while (!signal?.aborted) {
          const { value, done } = await reader.read();
          if (done) break;
          unzipperPush(value, false);
        }
        if (signal?.aborted) {
          reader.cancel().catch(() => undefined);
        } else {
          // Closing push — fflate signals "no more data" with an empty chunk.
          unzipperPush(new Uint8Array(0), true);
          resolve();
        }
      } catch (e) {
        reject(e);
      }
    })();
  });

  if (signal?.aborted) {
    return { groups: [], filesParsed: 0, filesSkipped: skippedNames.length, cancelled: true };
  }

  // Phase 2: parse every staged file's header to compute its signature, then
  // group files by signature.
  const parsed: Array<{ file: StagedFile; header: ParsedHeader }> = [];
  for (const stagedFile of staged) {
    const header = parseHeader(stagedFile.body, stagedFile.format);
    if (header === null) {
      skippedNames.push(stagedFile.name);
      continue;
    }
    parsed.push({ file: stagedFile, header });
  }

  const groups = new Map<string, { sheetName: string; columns: string[]; files: StagedFile[]; headers: ParsedHeader[] }>();
  for (const { file, header } of parsed) {
    const existing = groups.get(header.signature);
    if (existing !== undefined) {
      existing.files.push(file);
      existing.headers.push(header);
    } else {
      groups.set(header.signature, {
        sheetName: defaultSheetName(file.name),
        columns: header.columns,
        files: [file],
        headers: [header],
      });
    }
  }

  // Phase 3: ensure every group has a target sheet and import every file's
  // body. Files in the same group land in the same sheet, one after another.
  const createdSheets = new Set<string>([sheetId]);
  const results: ZipImportResult['groups'] = [];

  // The "current sheet" tells addSheet which id to create. We add one sheet
  // per group; the FIRST group reuses the user's active sheet.
  const groupEntries = [...groups.values()];
  const groupCount = groupEntries.length;

  for (let gi = 0; gi < groupCount; gi++) {
    if (signal?.aborted) break;
    const group = groupEntries[gi] as (typeof groupEntries)[number];
    let targetSheetId = sheetId;
    if (gi > 0) {
      // Add a fresh sheet for this group. The first group keeps the user's
      // current sheet; later groups each get their own.
      const addResult = await client.dispatch({ k: 'addSheet', name: group.sheetName });
      if (addResult.ok) {
        const view = await client.sheetView(addResult.ok ? '' : sheetId);
        // Use the sheet id the kernel reports back when present.
        const sheets = view.sheets;
        const added = sheets.find((s) => s.name === group.sheetName);
        targetSheetId = added?.id ?? sheetId;
        createdSheets.add(targetSheetId);
      }
    } else {
      // Group 0: ensure the current sheet name matches the group label so the
      // user can see which sheet holds the merged data.
      void client.dispatch({ k: 'renameSheet', sheet: sheetId, name: group.sheetName });
    }

    // Build a setCell batch per file. Header rows are re-written into each
    // file's block so columns survive a re-load.
    let totalRows = 0;
    let totalCells = 0;

    // Write the group's column headers as row 0 of the target sheet
    if (hasHeader && group.columns.length > 0) {
      const headerWidth = Math.min(group.columns.length, MAX_COLS);
      await client.dispatch({
        k: 'setRegion',
        range: {
          sheet: targetSheetId,
          r0: 0,
          c0: 0,
          r1: 0,
          c1: headerWidth - 1,
        },
        inputs: [group.columns.slice(0, headerWidth)],
      });
      totalCells += headerWidth;
    }

    const totalFilesInGroup = group.files.length;
    for (let fi = 0; fi < group.files.length; fi++) {
      if (signal?.aborted) break;
      const fileEntry = group.files[fi] as StagedFile;
      const header = group.headers[fi] as ParsedHeader;
      // Reconstruct a File object so we can reuse the single-file streaming
      // importer. The body is already decompressed; passing a synthetic File
      // keeps the streaming importer's surface unchanged.
      const synthetic = new File([fileEntry.body], fileEntry.name, {
        type: 'text/csv',
      });
      const fileRowLimit = Math.max(1, rowLimit - totalRows);
      const result = await streamSingleImport(
        client,
        synthetic,
        {
          sheetId: targetSheetId,
          delimiter: header.delimiter,
          hasHeader,
          rowLimit: fileRowLimit,
          batchSize: 8192,
        },
        signal,
      );
      totalRows += result.rows;
      totalCells += result.cells;
      opts.onProgress?.({
        file: fileEntry.name,
        rows: totalRows,
        sheet: group.sheetName,
        fileIndex: fi + 1,
        fileTotal: totalFilesInGroup,
      });
    }

    results.push({
      sheetName: group.sheetName,
      columns: group.columns,
      files: group.files.map((f) => f.name),
      rows: totalRows,
      cells: totalCells,
    });
  }

  return {
    groups: results,
    filesParsed: parsed.length,
    filesSkipped: skippedNames.length,
    cancelled: !!signal?.aborted,
  };
}

/** A safe sheet name from the file name (no slashes, unique-ish). */
function defaultSheetName(fileName: string): string {
  // Drop directory components and extension.
  const base = fileName.split('/').pop() ?? fileName;
  const stem = base.replace(/\.[^.]+$/, '');
  // Replace characters Excel forbids in sheet names with `_`.
  // Excel: \ / ? * [ ] :
  return stem.replace(/[\\/?*[\]:]/g, '_').slice(0, 28) || 'Imported';
}

/** A column-signature hash that the export side can use to know whether two
 * sheets share a layout. Kept here for symmetry with the import side. */
export function sheetColumnSignature(columns: readonly string[]): string {
  return columns.map((c) => c.toLowerCase()).join('\u0001');
}

// Suppress an unused-import warning for MAX_COLS — exposed for future
// extensions that may want to enforce per-file column bounds.
void MAX_COLS;
